import { Download, Eye, FileText, Film, Image as ImageIcon, MoreVertical, Play, RotateCcw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { pressReleasesApi } from '../pressReleaseApi';
import type { PressReleaseAttachmentItem } from '../pressReleaseTypes';
import { AttachFilesModal } from './AttachFilesModal';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';

/** The most a press release shows in one box — the API's own page ceiling. The box says when there are more. */
const LIMIT = 100;

/**
 * A press release's files as a LIST BOX of thumbnails — the product call, 12 Oct 2026. A tile opens the
 * file in a preview; its menu downloads, deletes or restores it.
 *
 * **What a tile shows is gated on `download`**, because a thumbnail of an image IS its contents:
 * without the key a tile draws its type's glyph and its name, and nothing opens. The names are
 * `read`'s, like any row.
 *
 * **Thumbnails are fetched per tile and re-typed to JPEG** through `useBlobPreview`, the one place
 * a fetched file becomes something the browser renders. The FILE is fetched only when a tile is
 * opened — up to 250 MB, never for a list.
 *
 * **Deleted files are hidden and can be shown** (the cog's Show deleted), marked Deleted, with
 * Restore in their menu.
 */
export function AttachmentsBox({
  releaseId,
  releaseTitle,
  lockReason,
  onChanged,
}: {
  releaseId: string;
  releaseTitle: string;
  /** Why the files cannot change right now (deleted, or published), or null — the server's lock, stated first. */
  lockReason: string | null;
  /** A file was attached, deleted or restored — the page refreshes its counts. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const canDownload = usePermission('press-releases.download');

  const [reloadKey, setReloadKey] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);

  /*
    The answer is stored WITH the request it answers, and loading is DERIVED from whether it
    answers the current one — so nothing resets state inside the effect, and a stale answer is
    never painted under a new request.
  */
  const requestKey = `${releaseId}:${showDeleted}:${reloadKey}`;
  const [answer, setAnswer] = useState<{
    key: string;
    items: PressReleaseAttachmentItem[] | null;
    total: number;
    error: ApiError | null;
  } | null>(null);

  const current = answer?.key === requestKey ? answer : null;
  const items = current?.items ?? null;
  const total = current?.total ?? 0;
  const error = current?.error ?? null;
  const [attaching, setAttaching] = useState(false);
  const [previewing, setPreviewing] = useState<PressReleaseAttachmentItem | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; file: PressReleaseAttachmentItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    pressReleasesApi
      .attachments(
        releaseId,
        { pageSize: LIMIT, sort: 'createdAt:asc', status: showDeleted ? undefined : 'active' },
        controller.signal,
      )
      .then((page) => {
        if (controller.signal.aborted) return;
        setAnswer({ key: requestKey, items: page.items, total: page.total, error: null });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setAnswer({
          key: requestKey,
          items: null,
          total: 0,
          error: caught instanceof ApiError ? caught : new ApiError('Could not load the files.', 0),
        });
      });

    return () => controller.abort();
  }, [releaseId, showDeleted, requestKey]);

  const download = useCallback(
    async (file: PressReleaseAttachmentItem) => {
      try {
        const { blob, fileName } = await pressReleasesApi.download(releaseId, file.id);

        // Re-typed to the VERIFIED type, so a mislabelled payload cannot pick its own handler.
        const url = URL.createObjectURL(new Blob([blob], { type: file.contentType }));
        const anchor = window.document.createElement('a');

        anchor.href = url;
        anchor.download = fileName ?? file.fileName;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        URL.revokeObjectURL(url);
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the file.');
      }
    },
    [releaseId, toast],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setActionError(null);

    try {
      if (confirming.kind === 'delete') await pressReleasesApi.removeAttachment(releaseId, confirming.file.id);
      else await pressReleasesApi.restoreAttachment(releaseId, confirming.file.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.file.fileName}.`);
      setConfirming(null);
      reload();
      onChanged();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }



  return (
    <>
      <ProfileCard
        id="press-release-profile-attachments"
        title="Attachments"
        count={items === null ? undefined : total}
        onEdit={() => setAttaching(true)}
        editLabel="Attach files"
        editPermission="press-releases.attach"
        editDisabledReason={lockReason}
        menuItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            checked: showDeleted,
            onClick: () => setShowDeleted((v) => !v),
          },
        ]}
        empty={
          items !== null && items.length === 0
            ? {
                title: showDeleted ? 'No file has ever been attached to this press release.' : 'No file is attached to this press release.',
                description: 'Videos, PDFs and images attached here are shown as thumbnails you can open.',
              }
            : undefined
        }
        padded
      >
        {error ? (
          <ErrorState message={error.message} onRetry={reload} />
        ) : items === null ? (
          <ul aria-busy="true" aria-label="Loading files" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <li key={i} className="flex flex-col gap-2">
                <Skeleton className="aspect-video w-full" />
                <Skeleton className="h-4 w-2/3" />
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col gap-3">
            <ul aria-label={`Files attached to ${releaseTitle}`} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((file) => (
                <AttachmentTile
                  key={file.id}
                  releaseId={releaseId}
                  file={file}
                  canDownload={canDownload}
                  onOpen={() => setPreviewing(file)}
                  menu={
                    <Menu
                      id={`press-release-attachment-${file.id}-menu`}
                      items={[
                        {
                          key: 'preview',
                          label: 'Preview',
                          icon: <Eye className="h-4 w-4" aria-hidden="true" />,
                          permission: 'press-releases.download',
                          onClick: () => setPreviewing(file),
                        },
                        {
                          key: 'download',
                          label: 'Download',
                          icon: <Download className="h-4 w-4" aria-hidden="true" />,
                          permission: 'press-releases.download',
                          onClick: () => void download(file),
                        },
                        file.isActive
                          ? {
                              key: 'delete',
                              label: 'Delete',
                              icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
                              permission: 'press-releases.attach',
                              destructive: true,
                              disabledReason: lockReason,
                              divider: true,
                              onClick: () => setConfirming({ kind: 'delete', file }),
                            }
                          : {
                              key: 'restore',
                              label: 'Restore',
                              icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                              permission: 'press-releases.attach',
                              disabledReason: lockReason,
                              divider: true,
                              onClick: () => setConfirming({ kind: 'restore', file }),
                            },
                      ]}
                      trigger={({ open, toggle }) => (
                        <button
                          type="button"
                          onClick={toggle}
                          aria-haspopup="menu"
                          aria-expanded={open}
                          aria-label={`${file.fileName} options`}
                          className="shrink-0 rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                        >
                          <MoreVertical className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    />
                  }
                />
              ))}
            </ul>

            {total > items.length ? (
              <p className="text-xs text-slate-500">
                Showing the first {items.length} of {total} files, in the order they were attached.
              </p>
            ) : null}
          </div>
        )}
      </ProfileCard>

      {attaching ? (
        <AttachFilesModal
          releaseId={releaseId}
          releaseTitle={releaseTitle}
          onClose={() => setAttaching(false)}
          onFiled={() => {
            reload();
            onChanged();
          }}
        />
      ) : null}

      {previewing && canDownload ? (
        <AttachmentPreviewModal
          releaseId={releaseId}
          attachment={previewing}
          onDownload={() => void download(previewing)}
          onClose={() => setPreviewing(null)}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete file' : 'Restore file'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`press-release-attachment-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `${confirming.file.fileName} leaves this press release's files. Its bytes are kept in the Repository, and it can be restored with Show deleted.`
                  : `${confirming.file.fileName} returns to this press release's files.`}
              </p>

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/** What a file IS, as a glyph — the tile's face when there is no thumbnail to show. */
function TypeGlyph({ contentType, className }: { contentType: string; className: string }) {
  if (contentType === 'video/mp4') return <Film className={className} aria-hidden="true" />;
  if (contentType.startsWith('image/')) return <ImageIcon className={className} aria-hidden="true" />;
  return <FileText className={className} aria-hidden="true" />;
}

/**
 * One tile: the thumbnail (or the type's glyph), the name, the type and size, and the menu.
 *
 * **The face is a BUTTON that opens the preview** — for a reader holding `download`. Without it the
 * face is inert and says nothing would open; the name and the menu stay.
 */
function AttachmentTile({
  releaseId,
  file,
  canDownload,
  onOpen,
  menu,
}: {
  releaseId: string;
  file: PressReleaseAttachmentItem;
  canDownload: boolean;
  onOpen: () => void;
  menu: ReactNode;
}) {
  const wantsThumbnail = canDownload && file.hasThumbnail;

  const load = useCallback(
    (signal: AbortSignal) => pressReleasesApi.thumbnail(releaseId, file.id, signal),
    [releaseId, file.id],
  );

  // A thumbnail is ALWAYS a JPEG — the server's category accepts nothing else — so it is pinned to that type.
  const thumbnail = useBlobPreview(wantsThumbnail ? `${file.id}:thumbnail` : undefined, 'image/jpeg', load);

  const isVideo = file.contentType === 'video/mp4';

  const face = (
    <span className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
      {thumbnail.url ? (
        <img src={thumbnail.url} alt="" className="h-full w-full object-cover" />
      ) : wantsThumbnail && thumbnail.loading ? (
        <Skeleton className="h-full w-full" />
      ) : (
        <TypeGlyph contentType={file.contentType} className="h-8 w-8 text-slate-400" />
      )}

      {isVideo && thumbnail.url ? (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900/65 text-white">
            <Play className="h-5 w-5" />
          </span>
        </span>
      ) : null}
    </span>
  );

  return (
    <li className="flex min-w-0 flex-col gap-1.5">
      {canDownload ? (
        <button
          type="button"
          id={`press-release-attachment-${file.id}-open`}
          onClick={onOpen}
          className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        >
          {face}
          <span className="sr-only">Preview {file.fileName}</span>
        </button>
      ) : (
        <span className="cursor-default">{face}</span>
      )}

      <div className="flex min-w-0 items-start gap-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* A long one-word name ellipsizes WITH a tooltip; a name with spaces wraps — `describeOverflow`. */}
          <TextCell value={file.fileName} />
          <TextCell value={formatBytes(file.sizeBytes)} mono size="xs" />
          {!file.isActive ? (
            <span className="mt-1 inline-block">
              <StatusText tone="inactive">Deleted</StatusText>
            </span>
          ) : null}
        </div>

        {menu}
      </div>
    </li>
  );
}
